import {
  ProductOption,
  ProductVariant,
  ProductWithVariants
} from 'types/product'
import { FilterOption } from '@helpers/filters/getFiltersOptionsState'
import { FINISHED_TO_ORDER } from '@consts/index'

const sortOrderOptionsByPosition = (a: FilterOption, b: FilterOption): number =>
  b.position < a.position ? 1 : -1

const isDataAvailable = (
  productOptions: ProductOption[],
  filterOptions: FilterOption[]
) => {
  return productOptions?.length > 0 && filterOptions?.length > 0
}

function getFilterOptionsWithPreselectedOverrides(
  filterOptions: FilterOption[],
  productOptions: ProductOption[]
) {
  const preselectedFilterOptionsOverrides = productOptions
    .map(productOption => {
      const optionTypeOverrideValue =
        productOption.values.length === 1 ? productOption.values[0] : null

      if (optionTypeOverrideValue) {
        return {
          type: productOption.type,
          value: optionTypeOverrideValue,
          position: productOption.position
        }
      }

      return null
    })
    .filter(Boolean)

  return filterOptions
    .filter(
      filterOption =>
        !preselectedFilterOptionsOverrides.find(
          override => override.type === filterOption.type
        )
    )
    .concat(preselectedFilterOptionsOverrides)
}

export default function getSelectedFilterOptions(
  productOptions: ProductOption[],
  filterOptions: FilterOption[]
) {
  if (!isDataAvailable(productOptions, filterOptions)) {
    return []
  }

  const filterOptionsWithPreSelectedOverrides =
    getFilterOptionsWithPreselectedOverrides(filterOptions, productOptions)

  return filterOptionsWithPreSelectedOverrides
    .filter(filterOption => {
      return productOptions
        .find(option => option.type === filterOption.type)
        ?.values?.includes(filterOption.value)
    })
    .filter(
      (filterOption, index, compatibleOptions) =>
        compatibleOptions.findIndex(
          option => option.type === filterOption.type
        ) === index
    )
    .sort(sortOrderOptionsByPosition)
}

type ProductSelectionData = Pick<ProductWithVariants, 'options' | 'variants'>

function groupSelectedValues(
  productOptions: ProductOption[],
  filterOptions: FilterOption[]
) {
  return productOptions.reduce<Record<string, string[]>>((groups, option) => {
    const selectedValues = filterOptions
      .filter(filter => filter.type === option.type)
      .map(filter => filter.value)
      .filter(value => option.values.includes(value))

    if (selectedValues.length) {
      return { ...groups, [option.type]: selectedValues }
    }

    return option.values.length === 1
      ? { ...groups, [option.type]: option.values }
      : groups
  }, {})
}

function variantMatchesGroups(
  variant: ProductVariant,
  groups: Record<string, string[]>,
  types = Object.keys(groups)
) {
  return types.every(type => {
    const variantValue = variant.options.find(
      option => option.type === type
    )?.value

    return (
      variantValue === FINISHED_TO_ORDER ||
      (!!variantValue && groups[type].includes(variantValue))
    )
  })
}

function compareVariantPriority(
  first: ProductVariant,
  second: ProductVariant,
  productOptions: ProductOption[],
  groups: Record<string, string[]>
) {
  for (const option of productOptions) {
    const selectedValues = groups[option.type]
    if (!selectedValues?.length) continue

    const firstValue = first.options.find(
      variantOption => variantOption.type === option.type
    )?.value
    const secondValue = second.options.find(
      variantOption => variantOption.type === option.type
    )?.value
    const firstSelectedIndex = selectedValues.indexOf(firstValue || '')
    const secondSelectedIndex = selectedValues.indexOf(secondValue || '')
    const firstPriority =
      firstValue === FINISHED_TO_ORDER
        ? 0
        : firstSelectedIndex === -1
        ? Number.MAX_SAFE_INTEGER
        : firstSelectedIndex
    const secondPriority =
      secondValue === FINISHED_TO_ORDER
        ? 0
        : secondSelectedIndex === -1
        ? Number.MAX_SAFE_INTEGER
        : secondSelectedIndex

    if (firstPriority !== secondPriority) return firstPriority - secondPriority
  }

  return 0
}

function getVariantFilterOptions(
  variant: ProductVariant,
  productOptions: ProductOption[],
  groups: Record<string, string[]>
) {
  return productOptions.flatMap<FilterOption>(option => {
    const variantValue = variant.options.find(
      variantOption => variantOption.type === option.type
    )?.value
    const value =
      variantValue === FINISHED_TO_ORDER
        ? groups[option.type]?.[0]
        : variantValue

    return value
      ? [{ type: option.type, value, position: option.position }]
      : []
  })
}

export function getSelectedFilterOptionsForProduct(
  product: ProductSelectionData,
  filterOptions: FilterOption[]
) {
  const { options, variants = [] } = product
  const hasCompatibleSelection = filterOptions.some(filter =>
    options.some(
      option =>
        option.type === filter.type && option.values.includes(filter.value)
    )
  )

  if (!hasCompatibleSelection) {
    return getSelectedFilterOptions(options, filterOptions)
  }

  const groups = groupSelectedValues(options, filterOptions)
  const hasMultiSelection = Object.values(groups).some(
    values => values.length > 1
  )

  if (!hasMultiSelection) {
    return getSelectedFilterOptions(options, filterOptions)
  }

  const inStockVariants = variants.filter(
    variant => variant.inventory_quantity > 0
  )
  const exactMatches = inStockVariants
    .filter(variant => variantMatchesGroups(variant, groups))
    .sort((first, second) =>
      compareVariantPriority(first, second, options, groups)
    )
  const primarySelectedType = options.find(option => groups[option.type])?.type
  const fallbackMatches = primarySelectedType
    ? inStockVariants
        .filter(variant =>
          variantMatchesGroups(variant, groups, [primarySelectedType])
        )
        .sort((first, second) =>
          compareVariantPriority(first, second, options, groups)
        )
    : []
  const selectedVariant = exactMatches[0] || fallbackMatches[0]

  return selectedVariant
    ? getVariantFilterOptions(selectedVariant, options, groups)
    : variants.length
    ? []
    : getSelectedFilterOptions(options, filterOptions)
}
